/**
 * Grafiki marek i modeli aut w kreatorze doboru ładowarki.
 *
 * Ta sama konwencja co logo producentów paneli i falowników: ścieżka liczona
 * z nazwy, a komponent chowa obrazek, gdy pliku nie ma. Dzięki temu katalog
 * uzupełnia się stopniowo — brak grafiki niczego nie psuje, po prostu jej nie
 * widać. Kolejne pliki wystarczy wrzucić do `public/`, bez zmian w kodzie.
 *
 * Nazwy plików: marka i model sprowadzone do małych liter bez polskich znaków,
 * spacje i znaki specjalne zamienione na myślnik. Przykłady:
 *   BMW              -> /car_brands/bmw.png
 *   Skoda Enyaq 85   -> /car_models/skoda-enyaq-85.jpg
 *
 * Dwa różne formaty, każdy z powodu: logotypy jako PNG, bo potrzebują
 * przezroczystego tła; zdjęcia aut jako JPG, bo to fotografie i w PNG ważyły
 * około dziesięciu razy więcej (69 MB zamiast 14 MB dla całego katalogu).
 * To ta sama konwencja co przy logotypach producentów paneli.
 */

import spis from '../data/grafikiAut.json';

/**
 * Zbiór grafik, które FAKTYCZNIE leżą w `public/`. Bez niego front zgadywał:
 * wskazywał ścieżkę wersji i liczył na zdarzenie `error`, gdy pliku nie ma.
 * To nie działa — nginx na brakujący plik statyczny nie zwraca 404, tylko 200
 * ze skorupą SPA, więc przeglądarka dostaje poprawną odpowiedź i zdarzenie
 * błędu się nie odpala. Zdjęcie po prostu nie pojawiało się, bez śladu w konsoli.
 *
 * Spis generuje `wallbox_scraper/pobierz_grafiki_aut.py --indeks`. Po dorzuceniu
 * albo usunięciu pliku w `public/` trzeba go przegenerować.
 */
const DOSTEPNE = new Set(spis);

const istnieje = (sciezka) => DOSTEPNE.has(String(sciezka).replace(/^\//, ''));

const slug = (tekst) =>
  String(tekst || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export const sciezkaLogaMarki = (marka) => {
  if (!marka) return null;
  const sciezka = `/car_brands/${slug(marka)}.png`;
  return istnieje(sciezka) ? sciezka : null;
};

/**
 * Ścieżki zdjęcia auta, od najbardziej do najmniej szczegółowej.
 *
 * Baza rozróżnia wersje pakietów baterii (Enyaq 60 i Enyaq 85 to osobne
 * wiersze), ale na zdjęciu to ten sam samochód. Wymaganie pliku per wersja
 * oznaczałoby 164 grafiki zamiast 49 — dlatego najpierw szukamy pliku wersji,
 * a gdy go nie ma, spadamy do zdjęcia modelu.
 *
 * Dzięki temu `skoda-enyaq.png` obsługuje wszystkie Enyaqi, a osobny plik
 * `skoda-enyaq-85.png` dorzuca się tylko wtedy, gdy naprawdę ma sens.
 *
 * Uwaga na zachowanie nginxa: brakujący plik statyczny NIE daje 404, tylko
 * 200 ze skorupą SPA (`text/html`). Spadek działa mimo to — przeglądarka nie
 * zdekoduje HTML-a jako obrazka i odpala `onError`. Kosztuje to jedno zbędne
 * żądanie na wersję bez własnego zdjęcia; przy kilku kilobajtach skorupy nie
 * warto tego optymalizować, ale bez tej uwagi mechanizm wygląda na przypadek.
 */
export const sciezkiZdjeciaAuta = (auto) => {
  if (!auto) return [];
  const model = [auto.brand, auto.model].filter(Boolean).join('-');
  return [
    auto.slug && `/car_models/${slug(auto.slug)}.jpg`,
    model && `/car_models/${slug(model)}.jpg`,
  ].filter(Boolean).filter(istnieje);
};
